/**
 * mergeUtils — pure helpers for cell merging in the SimpleExcelViewer.
 *
 * A merge is stored as a "region": { r, c, rowSpan, colSpan, center }.
 * The grid consumes a merge MAP keyed "r:c":
 *   master cell → { rowSpan, colSpan }
 *   slave cells → null   (so GridRow skips rendering them)
 *
 * Merging here is VIEW-ONLY: it changes how the current view displays,
 * never the underlying workbook.  Unmerging simply drops the region, so
 * the originally-hidden values reappear.
 */

/** Extract master regions from a "r:c" merge map (slaves are null). */
export function mergesToRegions(merges) {
  const regions = [];
  if (!merges) return regions;
  for (const key in merges) {
    const v = merges[key];
    if (v && v.rowSpan) {
      const sep = key.indexOf(":");
      const r = parseInt(key.slice(0, sep), 10);
      const c = parseInt(key.slice(sep + 1), 10);
      regions.push({ r, c, rowSpan: v.rowSpan, colSpan: v.colSpan, center: false });
    }
  }
  return regions;
}

/** Build a "r:c" merge map (master + null slaves) from a region list. */
export function regionsToMergeMap(regions) {
  const map = {};
  for (let i = 0; i < regions.length; i += 1) {
    const rg = regions[i];
    map[`${rg.r}:${rg.c}`] = { rowSpan: rg.rowSpan, colSpan: rg.colSpan };
    for (let r = rg.r; r < rg.r + rg.rowSpan; r += 1) {
      for (let c = rg.c; c < rg.c + rg.colSpan; c += 1) {
        if (r !== rg.r || c !== rg.c) map[`${r}:${c}`] = null;
      }
    }
  }
  return map;
}

/** True when a region overlaps the inclusive range [r1..r2] × [c1..c2]. */
export function regionIntersectsRange(rg, r1, c1, r2, c2) {
  const rEnd = rg.r + rg.rowSpan - 1;
  const cEnd = rg.c + rg.colSpan - 1;
  return rg.r <= r2 && rEnd >= r1 && rg.c <= c2 && cEnd >= c1;
}
